import { useState, useEffect, useCallback } from 'react';
import {
  adminGetStats,
  adminGetPurchases,
  adminGetSubscribers,
  adminGetCodes,
  type AdminStats,
  type PaginatedResponse,
  type PurchaseRow,
  type SubscriberRow,
  type CodeRow,
} from '../lib/api';

export function useAdminStats() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminGetStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { stats, loading, error, refresh };
}

export function useAdminPurchases() {
  const [data, setData] = useState<PaginatedResponse<PurchaseRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const result = await adminGetPurchases(page);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load purchases');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPage(1); }, [loadPage]);

  return { data, loading, error, loadPage };
}

export function useAdminSubscribers() {
  const [data, setData] = useState<PaginatedResponse<SubscriberRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const result = await adminGetSubscribers(page);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPage(1); }, [loadPage]);

  return { data, loading, error, loadPage };
}

export function useAdminCodes() {
  const [data, setData] = useState<PaginatedResponse<CodeRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const result = await adminGetCodes(page);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPage(1); }, [loadPage]);

  return { data, loading, error, loadPage };
}
